import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  CircularProgress,
} from '@mui/material';

import {
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';

import { PlanProfileForm } from './components';

import { planService, uploadService } from '../../services';
import { validator } from '../../utils';
import { useSnack } from '../../hooks';
import { CancelButton, PrimaryButton, SecondaryButton } from '../../components/styles/buttons/index';

function PlanInfoPage() {
  const { id } = useParams();
  const { setSnack } = useSnack();
  const navigate = useNavigate();

  const [state, setState] = useState({
    loading: true,
    edit: false,
    saving: false,
    dialogOpen: false,
    preSale: false,
  });

  const [profile, setProfile] = useState({});

  useEffect(() => {
    async function fetchProfile() {
      if (parseInt(id, 10)) {
        const loadedData = (await planService.get(id)) || {};
        setProfile(loadedData);
        setState((oldState) => ({
          ...oldState,
          loading: false,
          edit: !loadedData?.id,
          dialogOpen: false,
          preSale: loadedData?.discountPeriod != null,
        }));
      } else {
        setState((oldState) => ({
          ...oldState,
          loading: false,
          edit: true,
          dialogOpen: false,
        }));
      }
    }

    fetchProfile();

    return () => {};
  }, [id]);

  const [errors, setErrors] = useState({
    name: '',
    image: '',
  });

  function isNewPlan() {
    return profile?.id == null;
  }

  const cancel = () => {
    navigate('/plans');
  };

  const toggleEdit = () => {
    if (parseInt(profile?.id, 10)) {
      setState({
        ...state,
        edit: !state.edit,
      });
    } else {
      cancel();
    }
  };

  const togglePreSale = () => {
    if (!state.preSale === false) {
      setProfile({
        ...profile,
        discountValue: null,
        discountPeriod: null,
        voucherValidFrom: null,
      });
    }
    setState({
      ...state,
      preSale: !state.preSale,
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);

    setState({
      ...state,
      loading: false,
      saving: true,
    });

    const image = (profile.file?.size || 0) === 0
      ? profile.image
      : await uploadService.image(profile.file);

    profile.image = image;

    profile.name = data.get('name');
    profile.diamonds = data.get('diamonds');
    profile.vouchers = data.get('vouchers');
    profile.voucherPeriod = data.get('voucherPeriod');
    profile.discountValue = data.get('discountValue') || 0;
    profile.discountPeriod = data.get('discountPeriod') || null;
    profile.expiry = data.get('expiry');
    profile.value = data.get('value');
    profile.voucherValidFrom = data.get('voucherValidFrom');
    profile.voucherFixedExpiry = data.get('voucherFixedExpiry');

    const nameError = validator.name(profile.name, true);

    if (!nameError) {
      try {
        if (isNewPlan()) {
          await planService.create(profile);
        } else {
          await planService.update(profile);
        }

        setState({
          ...state,
          loading: false,
          saving: false,
          edit: isNewPlan(),
        });

        if (isNewPlan()) {
          setProfile({});
        }

        return setSnack({
          type: 'success',
          message: 'Cadastro salvo com sucesso',
        });
      } catch (e) {
        setState({
          ...state,
          saving: false,
        });
        return setSnack({ message: e.message });
      }
    } else {
      return setErrors({
        name: nameError,
      });
    }
  };

  const clearErrors = () => {
    const { name, image } = errors;

    // Avoid setState cycle
    if (name || image) {
      setErrors({
        name: '',
        image: '',
      });
    }
  };

  function handleDelete() {
    planService.delete(profile.id).then((success) => {
      if (success) {
        cancel();
        setSnack({
          type: 'success',
          message: 'Plano apagada com sucesso',
        });
      } else {
        setSnack();
      }
    }).catch((error) => {
      setSnack({ message: error.message });
    });
  }

  const onDialogClose = (confirm) => {
    setState({ ...state, dialogOpen: false });

    if (confirm) {
      handleDelete();
    }
  };

  const confirmDelete = () => {
    setState({ ...state, dialogOpen: true });
  };

  const buttonRow = () => {
    const buttons = [];

    if (state.edit) {
      if (!isNewPlan()) {
        buttons.push(
          <CancelButton
            key="deleteButton"
            onClick={confirmDelete}
            sx={{ mr: 2, color: 'error.main' }}
          >
            <DeleteIcon sx={{ mr: 2 }} />
            {' '}
            Deletar
          </CancelButton>,
        );
      }
      buttons.push(
        <SecondaryButton
          key="backButton"
          onClick={togglePreSale}
          sx={{ mx: 2 }}
          variant={state.preSale ? 'secondaryButton' : 'primaryButton'}
        >
          {state.preSale ? 'Desabilitar' : 'Habilitar'} Pré-Venda
        </SecondaryButton>,
      );
      buttons.push(
        <SecondaryButton
          key="cancelEditButton"
          onClick={toggleEdit}
          sx={{ mr: 2 }}
          variant="secondaryButton"
        >
          Cancelar
        </SecondaryButton>,
      );
      buttons.push(
        <PrimaryButton
          type="submit"
          key="submitbutton"
          variant="primaryButton"
        >
          Salvar
        </PrimaryButton>,
      );
    } else {
      buttons.push(
        <SecondaryButton
          key="backButton"
          onClick={cancel}
          sx={{ mx: 2 }}
          variant="secondaryButton"
        >
          Voltar
        </SecondaryButton>,
      );
      buttons.push(
        <PrimaryButton
          key="editbutton"
          variant="primaryButton"
          onClick={toggleEdit}
        >
          Editar
          <EditIcon sx={{ ml: 2 }} />
        </PrimaryButton>,
      );
    }

    return buttons;
  };

  return state.loading
    ? (
      <CircularProgress />
    )
    : (
      <Box sx={{ display: 'flex' }}>
        <PlanProfileForm
          handleSubmit={handleSubmit}
          errors={errors}
          clearErrors={clearErrors}
          profile={profile}
          setProfile={setProfile}
          isEditMode={state.edit}
          isNewPlan={isNewPlan()}
          buttons={buttonRow()}
          preSale={state.preSale}
          dialogOpen={state.dialogOpen}
          onDialogClose={onDialogClose}
          loading={state.saving}
        />
      </Box>
    );
}

export default PlanInfoPage;
